import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Plus,
  Users as UsersIcon,
  UserPlus,
  Shield,
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  Copy,
  Key,
  DollarSign,
  Target,
  Award,
  TrendingUp,
  Sparkles,
  AlertTriangle,
  ChevronRight,
  BarChart3,
  Clock,
  ArrowUpRight,
  CheckCircle2,
  Loader2,
  Lightbulb,
  ShieldCheck,
  Trash2,
  Eye,
  Pencil,
  ClipboardList,
  FileText,
  Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// AI Insights Panel
const AgentInsights = ({ agentId, getAuthHeaders }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const resp = await axios.post(`${API_URL}/api/admin/agent-performance/${agentId}/ai-insights`, {}, getAuthHeaders());
      setInsights(resp.data);
    } catch (e) {
      toast.error('Failed to generate AI insights');
    } finally {
      setLoading(false);
    }
  };

  if (!insights && !loading) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="gap-2 rounded-full"
        onClick={fetchInsights}
        data-testid={`ai-insights-btn-${agentId}`}
      >
        <Sparkles className="w-4 h-4 text-[#0071E3]" />
        Generate AI Insights
      </Button>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 p-4">
        <Loader2 className="w-4 h-4 animate-spin" />
        Analyzing performance with AI...
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 bg-gradient-to-br from-[#1B365D]/5 to-[#0071E3]/5 rounded-xl" data-testid={`ai-insights-panel-${agentId}`}>
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-[#1B365D] flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#0071E3]" />
          AI Performance Analysis
        </h4>
        <div className="flex items-center gap-2">
          <div className={`text-lg font-bold ${insights.performance_score >= 70 ? 'text-emerald-600' : insights.performance_score >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
            {insights.performance_score}/100
          </div>
          <Button variant="ghost" size="sm" onClick={fetchInsights} className="h-7">
            <Sparkles className="w-3 h-3" />
          </Button>
        </div>
      </div>
      <p className="text-sm text-slate-700">{insights.performance_summary}</p>
      {insights.strengths?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-emerald-700 mb-1">Strengths</p>
          {insights.strengths.map((s, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-slate-600">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
              {s}
            </div>
          ))}
        </div>
      )}
      {insights.risk_flags?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-red-600 mb-1">Risk Flags</p>
          {insights.risk_flags.map((r, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-slate-600">
              <AlertTriangle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
              {r}
            </div>
          ))}
        </div>
      )}
      {insights.improvement_suggestions?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-[#0071E3] mb-1">Suggestions</p>
          {insights.improvement_suggestions.map((s, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-slate-600">
              <Lightbulb className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
              {s}
            </div>
          ))}
        </div>
      )}
      <div className="pt-2 border-t border-slate-200">
        <p className="text-xs text-slate-500">
          <span className="font-medium">Recommended focus:</span> {insights.recommended_focus}
        </p>
      </div>
    </div>
  );
};

const Users = () => {
  const { getAuthHeaders, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [agentPerformance, setAgentPerformance] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', name: '', role: 'sales_agent' });
  const [inviteResult, setInviteResult] = useState(null);
  const [activeTab, setActiveTab] = useState('performance');
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [pendingQualification, setPendingQualification] = useState([]);
  const [onboardingData, setOnboardingData] = useState([]);

  const isSuperAdmin = currentUser?.email === 'media@ehiehr.com';
  const isAdmin = currentUser?.role === 'admin' || isSuperAdmin;
  const [permissions, setPermissions] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editPerms, setEditPerms] = useState({});
  const [savingPerms, setSavingPerms] = useState(false);
  const [permTemplates, setPermTemplates] = useState([]);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);

  const PERM_LABELS = {
    users_view: 'View Users', users_create: 'Create Users', users_edit: 'Edit Users',
    users_delete: 'Delete Users', users_manage_roles: 'Manage Roles & Permissions',
    system_settings: 'System Settings', audit_logs: 'View Audit Logs',
    leads_view: 'View Leads', leads_create: 'Create Leads', leads_edit: 'Edit Leads',
    leads_delete: 'Delete Leads', leads_qualify: 'Qualify Leads',
    commissions_view: 'View Commissions', commissions_edit: 'Edit Commissions',
    commissions_approve: 'Approve Commissions',
    dashboard_admin: 'Admin Dashboard', dashboard_sales: 'Sales Dashboard',
    content_manage: 'Manage Content', calendar_view: 'View Calendar',
    onboarding_access: 'Onboarding Documents',
  };
  const PERM_GROUPS = [
    { label: 'User Management', keys: ['users_view', 'users_create', 'users_edit', 'users_delete', 'users_manage_roles'] },
    { label: 'System & Admin', keys: ['system_settings', 'audit_logs', 'dashboard_admin', 'dashboard_sales'] },
    { label: 'Leads', keys: ['leads_view', 'leads_create', 'leads_edit', 'leads_delete', 'leads_qualify'] },
    { label: 'Commissions', keys: ['commissions_view', 'commissions_edit', 'commissions_approve'] },
    { label: 'Resources', keys: ['content_manage', 'calendar_view', 'onboarding_access'] },
  ];
  const ROLE_OPTIONS = [
    { value: 'admin', label: 'Admin', color: 'bg-blue-100 text-blue-700' },
    { value: 'sales_agent', label: 'Sales Agent', color: 'bg-emerald-100 text-emerald-700' },
    { value: 'inside_sales', label: 'Inside Sales', color: 'bg-amber-100 text-amber-700' },
  ];
  const ROLE_COLORS = {
    super_admin: 'bg-purple-100 text-purple-800',
    admin: 'bg-blue-100 text-blue-700',
    sales_agent: 'bg-emerald-100 text-emerald-700',
    inside_sales: 'bg-amber-100 text-amber-700',
    agent: 'bg-emerald-100 text-emerald-700',
  };
  const ROLE_DISPLAY = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    sales_agent: 'Sales Agent',
    inside_sales: 'Inside Sales',
    agent: 'Sales Agent',
  };

  const openPermEditor = async (user) => {
    try {
      const [permRes, tplRes] = await Promise.all([
        axios.get(`${API_URL}/api/permissions/user/${user.id}`, getAuthHeaders()),
        axios.get(`${API_URL}/api/permissions/templates`, getAuthHeaders()),
      ]);
      setEditPerms(permRes.data.permissions);
      setPermTemplates(tplRes.data);
      setEditingUser(user);
      setShowSaveTemplate(false);
      setNewTemplateName('');
    } catch (e) {
      toast.error('Failed to load permissions');
    }
  };

  const savePerms = async () => {
    if (!editingUser) return;
    setSavingPerms(true);
    try {
      await axios.put(`${API_URL}/api/permissions/user/${editingUser.id}`, { permissions: editPerms }, getAuthHeaders());
      toast.success(`Permissions updated for ${editingUser.name || editingUser.email}`);
      setEditingUser(null);
      fetchAuditLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSavingPerms(false);
  };

  const applyTemplate = (tpl) => {
    setEditPerms({ ...tpl.permissions });
    toast.success(`Applied "${tpl.name}" template`);
  };

  const handleSaveAsTemplate = async () => {
    const name = newTemplateName.trim();
    if (!name) return toast.error('Enter a template name');
    try {
      const res = await axios.post(`${API_URL}/api/permissions/templates`, { name, permissions: editPerms }, getAuthHeaders());
      setPermTemplates(prev => [...prev, res.data]);
      setShowSaveTemplate(false);
      setNewTemplateName('');
      toast.success(`Template "${name}" saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDeleteTemplate = async (tplId) => {
    try {
      await axios.delete(`${API_URL}/api/permissions/templates/${tplId}`, getAuthHeaders());
      setPermTemplates(prev => prev.filter(t => t.id !== tplId));
      toast.success('Template deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete template');
    }
  };

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/permissions/my`, getAuthHeaders());
      setPermissions(response.data.permissions);
    } catch { /* ignore */ }
  }, [getAuthHeaders]);

  const fetchAuditLogs = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const response = await axios.get(`${API_URL}/api/audit-logs?limit=100`, getAuthHeaders());
      setAuditLogs(response.data);
    } catch { /* ignore */ }
  }, [getAuthHeaders, isSuperAdmin]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, getAuthHeaders());
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchPerformance = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/admin/agent-performance`, getAuthHeaders());
      setAgentPerformance(resp.data);
    } catch (e) {
      console.error('Failed to load performance:', e);
    }
  }, [getAuthHeaders]);

  const fetchPaymentRequests = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/leads/payment-requests`, getAuthHeaders());
      setPaymentRequests(resp.data);
    } catch (e) {
      console.error('Failed to load payment requests:', e);
    }
  }, [getAuthHeaders]);

  const fetchPendingQualification = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/admin/leads-pending-qualification`, getAuthHeaders());
      setPendingQualification(resp.data);
    } catch (e) {
      console.error('Failed to load pending qualification:', e);
    }
  }, [getAuthHeaders]);

  const fetchInvitations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/invitations`, getAuthHeaders());
      setInvitations(response.data);
    } catch (error) {
      console.error('Failed to load invitations:', error);
    }
  }, [getAuthHeaders]);

  const fetchOnboarding = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/admin/onboarding`, getAuthHeaders());
      setOnboardingData(resp.data);
    } catch (e) {
      console.error('Failed to load onboarding:', e);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchUsers();
    fetchPerformance();
    fetchPaymentRequests();
    fetchPendingQualification();
    fetchInvitations();
    fetchOnboarding();
    fetchPermissions();
    fetchAuditLogs();
  }, [fetchUsers, fetchPerformance, fetchPaymentRequests, fetchPendingQualification, fetchInvitations, fetchOnboarding, fetchPermissions, fetchAuditLogs]);

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/api/users/invite`, inviteData, getAuthHeaders());
      toast.success('User invited successfully');
      setInviteResult(response.data);
      fetchUsers();
      fetchInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const response = await axios.put(`${API_URL}/api/users/${userId}/toggle-active`, {}, getAuthHeaders());
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('Reset password for this user?')) return;
    try {
      const response = await axios.put(`${API_URL}/api/users/${userId}/reset-password`, {}, getAuthHeaders());
      toast.success(`New temporary password: ${response.data.temp_password}`);
      navigator.clipboard.writeText(response.data.temp_password);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handlePromoteToAdmin = async (userId) => handleChangeRole(userId, 'admin');
  const handleDemoteFromAdmin = async (userId) => handleChangeRole(userId, 'sales_agent');

  const handleChangeRole = async (userId, newRole) => {
    const label = ROLE_DISPLAY[newRole] || newRole;
    if (!window.confirm(`Change this user's role to ${label}?`)) return;
    try {
      await axios.put(`${API_URL}/api/permissions/user/${userId}/role`, { role: newRole }, getAuthHeaders());
      toast.success(`Role changed to ${label}`);
      fetchUsers();
      fetchPerformance();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change role');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${userName}"? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/users/${userId}`, getAuthHeaders());
      toast.success(`User "${userName}" deleted`);
      fetchUsers();
      fetchPerformance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleApprovePayment = async (leadId) => {
    if (!window.confirm('Approve this payment request?')) return;
    setActionLoading(leadId);
    try {
      await axios.post(`${API_URL}/api/leads/${leadId}/approve-payment`, {}, getAuthHeaders());
      toast.success('Payment approved');
      setPaymentRequests(prev => prev.filter(l => l.id !== leadId));
      fetchPerformance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve payment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAdminQualify = async (leadId) => {
    if (!window.confirm('Qualify this lead? Agent will be able to request payment.')) return;
    setActionLoading(leadId);
    try {
      await axios.post(`${API_URL}/api/leads/${leadId}/admin-qualify`, {}, getAuthHeaders());
      toast.success('Lead qualified!');
      fetchPerformance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to qualify lead');
    } finally {
      setActionLoading(null);
    }
  };

  const resetInviteModal = () => {
    setInviteData({ email: '', name: '', role: 'sales_agent' });
    setInviteResult(null);
    setShowInviteModal(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const totalCommission = users.reduce((sum, u) => sum + (u.commission_earned || 0), 0);
  const totalAgents = agentPerformance.length;
  const totalLeadsAll = agentPerformance.reduce((sum, a) => sum + a.total_leads, 0);
  const avgQualRate = totalAgents > 0 ? (agentPerformance.reduce((sum, a) => sum + a.qualification_rate, 0) / totalAgents) : 0;

  const handleExportUsersCSV = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/export/users`, {
        ...getAuthHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'users_export.csv';
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('Users exported to CSV');
    } catch {
      toast.error('Failed to export users');
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#1B365D] tracking-tight">User Management</h1>
          <p className="text-slate-500 mt-1">Oversee agent performance, verify leads, and manage payouts</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={handleExportUsersCSV}
            data-testid="export-users-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          {isSuperAdmin && <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
            <DialogTrigger asChild>
              <Button className="bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full" data-testid="invite-user-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Invite User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Invite New User</DialogTitle>
              </DialogHeader>
              {!inviteResult ? (
                <form onSubmit={handleInvite} className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Full Name *</Label>
                    <Input value={inviteData.name} onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })} placeholder="John Smith" required data-testid="invite-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Address *</Label>
                    <Input type="email" value={inviteData.email} onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })} placeholder="john@company.com" required data-testid="invite-email" />
                  </div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select value={inviteData.role} onValueChange={(v) => setInviteData({ ...inviteData, role: v })}>
                      <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin"><div className="flex items-center gap-2"><Shield className="w-4 h-4" />Admin</div></SelectItem>
                        <SelectItem value="sales_agent"><div className="flex items-center gap-2"><UsersIcon className="w-4 h-4" />Sales Agent</div></SelectItem>
                        <SelectItem value="inside_sales"><div className="flex items-center gap-2"><Eye className="w-4 h-4" />Inside Sales</div></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={resetInviteModal}>Cancel</Button>
                    <Button type="submit" className="bg-[#1B365D] hover:bg-[#1B365D]/90" data-testid="submit-invite-btn">Send Invitation</Button>
                  </div>
                </form>
              ) : (
                <div className="py-4 space-y-4">
                  <div className="p-4 bg-green-50 rounded-xl text-center">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="font-medium text-green-900">User Invited Successfully!</p>
                  </div>
                  <div className="space-y-4 bg-slate-50 p-4 rounded-xl">
                    <div>
                      <Label className="text-slate-500 text-xs">Name</Label>
                      <p className="font-medium">{inviteResult.name}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Email</Label>
                      <p className="font-medium">{inviteResult.email}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Temporary Password</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input value={inviteResult.temp_password} readOnly className="font-mono bg-white" />
                        <Button variant="outline" size="icon" onClick={() => copyToClipboard(inviteResult.temp_password)}><Copy className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Login URL</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input value={inviteResult.login_url} readOnly className="text-xs bg-white" />
                        <Button variant="outline" size="icon" onClick={() => copyToClipboard(inviteResult.login_url)}><Copy className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </div>
                  <Button onClick={resetInviteModal} className="w-full bg-[#1B365D] hover:bg-[#1B365D]/90">Done</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-0 shadow-apple">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Total Users</p>
                <p className="text-2xl font-bold text-[#1B365D] mt-1">{users.length}</p>
              </div>
              <div className="p-2.5 bg-[#1B365D]/10 rounded-lg"><UsersIcon className="w-5 h-5 text-[#1B365D]" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-apple">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Registered Agents</p>
                <p className="text-2xl font-bold text-[#0071E3] mt-1">{totalAgents}</p>
              </div>
              <div className="p-2.5 bg-[#0071E3]/10 rounded-lg"><Target className="w-5 h-5 text-[#0071E3]" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-apple">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Total Leads</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{totalLeadsAll}</p>
              </div>
              <div className="p-2.5 bg-slate-100 rounded-lg"><BarChart3 className="w-5 h-5 text-slate-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-apple">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Avg Qual Rate</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">{avgQualRate.toFixed(1)}%</p>
              </div>
              <div className="p-2.5 bg-purple-100 rounded-lg"><TrendingUp className="w-5 h-5 text-purple-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-apple border-l-4 border-l-amber-400">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Pending Payouts</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{paymentRequests.length}</p>
              </div>
              <div className="p-2.5 bg-amber-100 rounded-lg"><DollarSign className="w-5 h-5 text-amber-600" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 rounded-full">
          <TabsTrigger value="performance" className="rounded-full gap-1" data-testid="tab-performance">
            <BarChart3 className="w-4 h-4" /> Agent Performance
          </TabsTrigger>
          <TabsTrigger value="verification" className="rounded-full gap-1" data-testid="tab-verification">
            <ShieldCheck className="w-4 h-4" /> Lead Verification
            {(paymentRequests.length + pendingQualification.length) > 0 && <Badge className="ml-1 bg-amber-500 text-white text-[10px] px-1.5">{paymentRequests.length + pendingQualification.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="users" className="rounded-full gap-1" data-testid="tab-users">
            <UsersIcon className="w-4 h-4" /> All Users
          </TabsTrigger>
          <TabsTrigger value="invitations" className="rounded-full" data-testid="tab-invitations">Invitations</TabsTrigger>
          <TabsTrigger value="onboarding" className="rounded-full gap-1" data-testid="tab-onboarding">
            <ClipboardList className="w-4 h-4" /> Onboarding
            {onboardingData.filter(a => !a.onboarding_completed).length > 0 && <Badge className="ml-1 bg-amber-500 text-white text-[10px] px-1.5">{onboardingData.filter(a => !a.onboarding_completed).length}</Badge>}
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="audit" className="rounded-full gap-1" data-testid="tab-audit">
              <Clock className="w-4 h-4" /> Audit Log
            </TabsTrigger>
          )}
        </TabsList>

        {/* PERFORMANCE TAB */}
        <TabsContent value="performance" className="mt-6 space-y-4">
          {agentPerformance.length === 0 ? (
            <Card className="border-0 shadow-apple">
              <CardContent className="p-12 text-center text-slate-400">
                <UsersIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No agent data available</p>
                <p className="text-sm mt-1">Invite agents to start tracking performance</p>
              </CardContent>
            </Card>
          ) : (
            agentPerformance.map(agent => (
              <Card key={agent.id} className="border-0 shadow-apple overflow-hidden" data-testid={`agent-perf-${agent.id}`}>
                <div
                  className="p-5 cursor-pointer hover:bg-slate-50/50 transition-colors"
                  onClick={() => setExpandedAgent(expandedAgent === agent.id ? null : agent.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-full bg-[#1B365D] flex items-center justify-center text-white font-bold text-lg">
                        {agent.name?.charAt(0)?.toUpperCase() || 'A'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900">{agent.name}</h3>
                          {!agent.is_active && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                        </div>
                        <p className="text-sm text-slate-500">{agent.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      {/* Key Metrics Inline */}
                      <div className="hidden md:flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-lg font-bold text-slate-800">{agent.total_leads}</p>
                          <p className="text-[10px] text-slate-500 uppercase">Leads</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-[#0071E3]">{agent.demo_completion_rate}%</p>
                          <p className="text-[10px] text-slate-500 uppercase">Demo Rate</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-purple-600">{agent.qualification_rate}%</p>
                          <p className="text-[10px] text-slate-500 uppercase">Qual Rate</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-emerald-600">${agent.commission_earned?.toLocaleString() || 0}</p>
                          <p className="text-[10px] text-slate-500 uppercase">Commission</p>
                        </div>
                      </div>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedAgent === agent.id ? 'rotate-90' : ''}`} />
                    </div>
                  </div>
                </div>

                {/* Expanded Detail */}
                {expandedAgent === agent.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className="border-t border-slate-100"
                  >
                    <div className="p-5 space-y-5">
                      {/* Detailed Metrics Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                        {[
                          { label: 'Total Leads', value: agent.total_leads, color: 'text-slate-800' },
                          { label: 'Demos Done', value: agent.demo_completed, color: 'text-[#0071E3]' },
                          { label: 'Qualified', value: agent.qualified, color: 'text-purple-600' },
                          { label: 'Payment Pending', value: agent.payment_requested, color: 'text-amber-600' },
                          { label: 'Paid Out', value: agent.payment_approved, color: 'text-emerald-600' },
                          { label: 'Closed Won', value: agent.closed_won, color: 'text-emerald-700' },
                          { label: 'Closed Lost', value: agent.closed_lost, color: 'text-red-500' },
                        ].map(m => (
                          <div key={m.label} className="bg-slate-50 rounded-lg p-3 text-center">
                            <p className={`text-xl font-bold ${m.color}`}>{m.value}</p>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wide mt-0.5">{m.label}</p>
                          </div>
                        ))}
                      </div>

                      {/* Progress Bars */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-600">Demo Completion Rate</span>
                            <span className="font-semibold text-[#0071E3]">{agent.demo_completion_rate}%</span>
                          </div>
                          <Progress value={agent.demo_completion_rate} className="h-2" />
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-600">Lead Qualification Rate</span>
                            <span className="font-semibold text-purple-600">{agent.qualification_rate}%</span>
                          </div>
                          <Progress value={agent.qualification_rate} className="h-2" />
                        </div>
                      </div>

                      {/* Timing Metrics */}
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-600">Avg submit-to-demo:</span>
                          <span className="font-semibold">{agent.avg_days_to_demo || '-'} days</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-600">Avg demo-to-qualified:</span>
                          <span className="font-semibold">{agent.avg_days_demo_to_qualified || '-'} days</span>
                        </div>
                      </div>

                      {/* About Me Section */}
                      {(agent.about_me?.expertise || agent.about_me?.business_plan || agent.about_me?.focus_areas) && (
                        <div className="bg-slate-50 rounded-xl p-4 space-y-3" data-testid={`agent-about-${agent.id}`}>
                          <h4 className="font-semibold text-[#1B365D] text-sm flex items-center gap-1.5">
                            <User className="w-4 h-4" />
                            Agent Profile
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {agent.about_me?.expertise && (
                              <div>
                                <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium mb-1">Sales Expertise</p>
                                <p className="text-sm text-slate-700 whitespace-pre-line">{agent.about_me.expertise}</p>
                              </div>
                            )}
                            {agent.about_me?.business_plan && (
                              <div>
                                <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium mb-1">Business Plan</p>
                                <p className="text-sm text-slate-700 whitespace-pre-line">{agent.about_me.business_plan}</p>
                              </div>
                            )}
                            {agent.about_me?.focus_areas && (
                              <div>
                                <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium mb-1">Focus Areas</p>
                                <p className="text-sm text-slate-700 whitespace-pre-line">{agent.about_me.focus_areas}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* AI Insights */}
                      <AgentInsights agentId={agent.id} getAuthHeaders={getAuthHeaders} />
                    </div>
                  </motion.div>
                )}
              </Card>
            ))
          )}
        </TabsContent>

        {/* VERIFICATION & PAYOUT TAB */}
        <TabsContent value="verification" className="mt-6 space-y-6">
          {/* Leads Pending Qualification */}
          <Card className="border-0 shadow-apple" data-testid="pending-qualification-section">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-purple-500" />
                Leads Pending Qualification
                {pendingQualification.length > 0 && <Badge className="ml-2 bg-purple-500 text-white">{pendingQualification.length}</Badge>}
              </CardTitle>
              <CardDescription>Leads with completed demos awaiting admin qualification</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingQualification.length > 0 ? (
                <div className="space-y-3">
                  {pendingQualification.map(lead => (
                    <div key={lead.id} className="flex items-center justify-between p-4 bg-purple-50/50 rounded-xl border border-purple-100" data-testid={`qualify-lead-${lead.id}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <ShieldCheck className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{lead.organization_name || lead.contact_name}</p>
                          <p className="text-xs text-slate-500">
                            Demo completed {lead.demo_completed_at ? new Date(lead.demo_completed_at).toLocaleDateString() : 'recently'}
                          </p>
                        </div>
                      </div>
                      <Button
                        className="bg-[#8B5CF6] hover:bg-[#7C3AED] rounded-full text-white"
                        size="sm"
                        onClick={() => handleAdminQualify(lead.id)}
                        disabled={actionLoading === lead.id}
                        data-testid={`qualify-btn-${lead.id}`}
                      >
                        {actionLoading === lead.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
                        Qualify Lead
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p>No leads pending qualification</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Payment Requests */}
          <Card className="border-0 shadow-apple" data-testid="payment-requests-section">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-amber-500" />
                Pending Payment Requests
              </CardTitle>
              <CardDescription>Leads with approved qualification awaiting payout approval</CardDescription>
            </CardHeader>
            <CardContent>
              {paymentRequests.length > 0 ? (
                <div className="space-y-3">
                  {paymentRequests.map(lead => (
                    <div key={lead.id} className="flex items-center justify-between p-4 bg-amber-50/50 rounded-xl border border-amber-100" data-testid={`payment-request-${lead.id}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <DollarSign className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{lead.organization_name || lead.contact_name}</p>
                          <p className="text-xs text-slate-500">
                            Requested {lead.payment_requested_at ? new Date(lead.payment_requested_at).toLocaleDateString() : 'recently'}
                            {lead.owner_name && ` by ${lead.owner_name}`}
                          </p>
                        </div>
                      </div>
                      <Button
                        className="bg-emerald-600 hover:bg-emerald-700 rounded-full text-white"
                        size="sm"
                        onClick={() => handleApprovePayment(lead.id)}
                        disabled={actionLoading === lead.id}
                        data-testid={`approve-payout-${lead.id}`}
                      >
                        {actionLoading === lead.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                        Approve Payout
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p>No pending payment requests</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lead Workflow Reference */}
          <Card className="border-0 shadow-apple bg-gradient-to-br from-[#1B365D]/5 to-[#0071E3]/5">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ArrowUpRight className="w-5 h-5 text-[#0071E3]" />
                Lead Lifecycle Workflow
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  { label: 'Agent Submits', color: '#64748B' },
                  { label: 'Demo Scheduled', color: '#0071E3' },
                  { label: 'Demo Completed', color: '#F59E0B' },
                  { label: 'Admin Qualifies', color: '#8B5CF6' },
                  { label: 'Agent Requests $', color: '#EC4899' },
                  { label: 'Admin Approves $', color: '#10B981' },
                ].map((step, i, arr) => (
                  <div key={step.label} className="flex items-center gap-2">
                    <div className="px-3 py-1.5 rounded-full text-white text-xs font-medium" style={{ backgroundColor: step.color }}>
                      {step.label}
                    </div>
                    {i < arr.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300" />}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ALL USERS TAB */}
        <TabsContent value="users" className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-pulse text-slate-500">Loading users...</div>
            </div>
          ) : (
            <Card className="border-0 shadow-apple">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-100">
                      <tr>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-6">User</th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-6">Role</th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-6">Commission</th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-6">Joined</th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-6">Status</th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-6">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {users.map(u => (
                        <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-[#1B365D] flex items-center justify-center text-white font-semibold">
                                {u.name?.charAt(0)?.toUpperCase() || 'U'}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{u.name}</p>
                                <p className="text-sm text-slate-500">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <Badge className={`${ROLE_COLORS[u.role] || ROLE_COLORS.agent} text-[10px] border-0`}>
                              {(u.role === 'admin' || u.role === 'super_admin') && <Shield className="w-3 h-3 mr-1" />}
                              {ROLE_DISPLAY[u.role] || u.role}
                            </Badge>
                          </td>
                          <td className="py-4 px-6">
                            <p className="font-semibold text-emerald-600">${u.commission_earned?.toLocaleString() || 0}</p>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Calendar className="w-4 h-4" />
                              {new Date(u.created_at).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            {u.is_active ? (
                              <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-slate-500"><XCircle className="w-3 h-3 mr-1" />Inactive</Badge>
                            )}
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2">
                              {isSuperAdmin && <Switch checked={u.is_active} onCheckedChange={() => handleToggleActive(u.id)} data-testid={`toggle-user-${u.id}`} />}
                              {isSuperAdmin && <Button variant="ghost" size="sm" onClick={() => handleResetPassword(u.id)} title="Reset Password"><Key className="w-4 h-4" /></Button>}
                              {isSuperAdmin && currentUser?.email !== u.email && (
                                <Select value={u.role === 'agent' ? 'sales_agent' : u.role} onValueChange={(v) => handleChangeRole(u.id, v)}>
                                  <SelectTrigger className="w-[130px] h-8 text-xs" data-testid={`role-select-${u.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ROLE_OPTIONS.map(r => (
                                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              {isSuperAdmin && currentUser?.email !== u.email && (
                                <Button variant="ghost" size="sm" onClick={() => openPermEditor(u)} title="Edit Permissions" className="text-slate-600 hover:text-[#0071E3] hover:bg-blue-50" data-testid={`edit-perms-${u.id}`}><Pencil className="w-4 h-4" /></Button>
                              )}
                              {isSuperAdmin && currentUser?.email !== u.email && (
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id, u.name || u.email)} title="Delete User" className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                              )}
                              {!isSuperAdmin && <span className="text-xs text-slate-400">View only</span>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* INVITATIONS TAB */}
        <TabsContent value="invitations" className="mt-6">
          <Card className="border-0 shadow-apple">
            <CardHeader><CardTitle className="font-heading text-lg">Invitation History</CardTitle></CardHeader>
            <CardContent>
              {invitations.length > 0 ? (
                <div className="space-y-3">
                  {invitations.map(inv => (
                    <div key={inv.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1B365D]/10 flex items-center justify-center"><Mail className="w-5 h-5 text-[#1B365D]" /></div>
                        <div>
                          <p className="font-medium">{inv.email}</p>
                          <p className="text-sm text-slate-500">Invited by {inv.invited_by_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge variant={inv.status === 'pending' ? 'secondary' : 'default'} className="capitalize">{inv.status}</Badge>
                        <span className="text-sm text-slate-500">{new Date(inv.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No invitations yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ONBOARDING TAB */}
        <TabsContent value="onboarding" className="mt-6 space-y-4">
          <Card className="border-0 shadow-apple">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[#1B365D]" />
                Agent Onboarding Status
              </CardTitle>
              <CardDescription>Track contractor agreement, direct deposit, referral agreement, and W-9 completion.</CardDescription>
            </CardHeader>
            <CardContent>
              {onboardingData.length > 0 ? (
                <div className="space-y-3">
                  {onboardingData.map(agent => {
                    const pct = Math.round((agent.completed_steps / agent.total_steps) * 100);
                    return (
                      <div key={agent.agent_id} className="p-4 bg-slate-50 rounded-xl" data-testid={`onboarding-agent-${agent.agent_id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${agent.onboarding_completed ? 'bg-green-500' : 'bg-[#1B365D]'}`}>
                              {agent.agent_name?.charAt(0) || '?'}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{agent.agent_name}</p>
                              <p className="text-xs text-slate-500">{agent.agent_email}</p>
                            </div>
                          </div>
                          <Badge variant={agent.onboarding_completed ? 'default' : 'secondary'} className={agent.onboarding_completed ? 'bg-green-600' : agent.saved_for_later ? 'bg-amber-500 text-white' : ''}>
                            {agent.onboarding_completed ? 'Complete' : agent.saved_for_later ? 'Saved for Later' : `${agent.completed_steps}/${agent.total_steps} Steps`}
                          </Badge>
                        </div>
                        <Progress value={pct} className="h-1.5 mb-3" />
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                          {['contractor_agreement', 'direct_deposit', 'referral_agreement', 'w9', 'review'].map(stepKey => {
                            const step = agent.steps?.[stepKey] || {};
                            const isComplete = step.status === 'completed';
                            const labels = { contractor_agreement: 'Contract', direct_deposit: 'Direct Deposit', referral_agreement: 'Referral', w9: 'W-9', review: 'Review' };
                            return (
                              <div key={stepKey} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs ${isComplete ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                {isComplete ? <CheckCircle className="w-3 h-3 flex-shrink-0" /> : <Clock className="w-3 h-3 flex-shrink-0" />}
                                <span className="truncate">{labels[stepKey]}</span>
                                {isComplete && (step.uploaded_file || step.signature) && (
                                  <a href={`${API_URL}/api/admin/onboarding/${agent.agent_id}/download/${stepKey}`} target="_blank" rel="noopener noreferrer" className="ml-auto" onClick={e => e.stopPropagation()}>
                                    <Download className="w-3 h-3 text-[#0071E3]" />
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {agent.completed_at && <p className="text-[10px] text-slate-400 mt-2">Completed {new Date(agent.completed_at).toLocaleDateString()}</p>}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No agents have started onboarding yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AUDIT LOG TAB (Super Admin Only) */}
        {isSuperAdmin && (
        <TabsContent value="audit" className="mt-6">
          <Card className="border-0 shadow-apple">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#1B365D]" /> Activity Audit Log
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={async () => {
                  try {
                    const response = await axios.get(`${API_URL}/api/export/audit-logs`, {
                      ...getAuthHeaders(),
                      responseType: 'blob',
                    });
                    const blob = new Blob([response.data], { type: 'text/csv' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = 'audit_logs_export.csv';
                    link.click();
                    URL.revokeObjectURL(link.href);
                    toast.success('Audit logs exported to CSV');
                  } catch {
                    toast.error('Failed to export audit logs');
                  }
                }}
                data-testid="export-audit-logs-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No audit log entries yet. Actions like user creation, deletion, and role changes will appear here.</p>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {auditLogs.map((log, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 text-sm" data-testid={`audit-log-${i}`}>
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                        log.action.includes('delete') ? 'bg-red-500' :
                        log.action.includes('promote') ? 'bg-emerald-500' :
                        log.action.includes('demote') ? 'bg-amber-500' :
                        'bg-blue-500'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-700">{log.details || log.action}</p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          by {log.user_email} &middot; {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 flex-shrink-0">{log.action}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>

      {/* Permissions Editor Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => { if (!open) { setEditingUser(null); setShowSaveTemplate(false); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#1B365D]" />
              Edit Permissions
            </DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-5 pt-1">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-[#1B365D] flex items-center justify-center text-white text-sm font-bold">
                  {(editingUser.name || editingUser.email || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">{editingUser.name || 'Unnamed'}</p>
                  <p className="text-xs text-slate-500">{editingUser.email}</p>
                </div>
                <Badge className={`${ROLE_COLORS[editingUser.role] || ROLE_COLORS.agent} text-[10px] border-0`}>
                  {ROLE_DISPLAY[editingUser.role] || editingUser.role}
                </Badge>
              </div>
              <p className="text-xs text-slate-500 -mt-2 px-1">Permissions below override the default <strong>{ROLE_DISPLAY[editingUser.role] || editingUser.role}</strong> role settings.</p>

              {/* Templates Section */}
              <div className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid="permission-templates-section">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Quick Templates</p>
                <div className="flex flex-wrap gap-2">
                  {permTemplates.map(tpl => (
                    <div key={tpl.id} className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs rounded-full h-7 px-3 hover:bg-[#0071E3]/10 hover:text-[#0071E3] hover:border-[#0071E3]/30"
                        onClick={() => applyTemplate(tpl)}
                        title={tpl.description || tpl.name}
                        data-testid={`apply-template-${tpl.id}`}
                      >
                        {tpl.name}
                      </Button>
                      {!tpl.is_default && (
                        <button
                          onClick={() => handleDeleteTemplate(tpl.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          title="Delete template"
                          data-testid={`delete-template-${tpl.id}`}
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {!showSaveTemplate ? (
                  <button
                    onClick={() => setShowSaveTemplate(true)}
                    className="text-xs text-[#0071E3] hover:underline mt-1"
                    data-testid="save-as-template-btn"
                  >
                    + Save current as template
                  </button>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      value={newTemplateName}
                      onChange={(e) => setNewTemplateName(e.target.value)}
                      placeholder="Template name..."
                      className="h-7 text-xs flex-1"
                      data-testid="new-template-name"
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveAsTemplate()}
                    />
                    <Button size="sm" className="h-7 text-xs bg-[#0071E3] hover:bg-[#0071E3]/90 rounded-full px-3" onClick={handleSaveAsTemplate} data-testid="confirm-save-template">
                      Save
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => { setShowSaveTemplate(false); setNewTemplateName(''); }}>
                      <XCircle className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>

              {PERM_GROUPS.map(group => (
                <div key={group.label}>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{group.label}</p>
                  <div className="space-y-1">
                    {group.keys.map(key => (
                      <div key={key} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50">
                        <span className="text-sm text-slate-700">{PERM_LABELS[key]}</span>
                        <Switch
                          checked={!!editPerms[key]}
                          onCheckedChange={(val) => setEditPerms(prev => ({ ...prev, [key]: val }))}
                          data-testid={`perm-toggle-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="flex gap-2 pt-2">
                <Button onClick={savePerms} disabled={savingPerms} className="flex-1 bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full" data-testid="save-permissions">
                  {savingPerms ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Permissions
                </Button>
                <Button variant="outline" onClick={() => setEditingUser(null)} className="rounded-full">Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Commission Structure */}
      <Card className="border-0 shadow-apple bg-gradient-to-br from-[#1B365D]/5 to-[#0071E3]/5">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Commission Structure
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">Qualified Lead Commission</h4>
              <div className="p-4 bg-white rounded-xl border border-slate-200">
                <p className="text-2xl font-bold text-emerald-600">$200</p>
                <p className="text-sm text-slate-500 mt-1">Flat fee per qualified referral</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">Closed Deal Commission</h4>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { product: 'EHR', amount: 500 },
                  { product: 'RCM', amount: 400 },
                  { product: 'Practice Mgmt', amount: 350 },
                  { product: 'CCM', amount: 300 },
                  { product: 'RPM', amount: 300 },
                  { product: 'AWV', amount: 200 },
                  { product: 'Bundle', amount: 800 },
                ].map(item => (
                  <div key={item.product} className="flex items-center justify-between p-2 bg-white rounded-lg border border-slate-100">
                    <span className="text-sm text-slate-600">{item.product}</span>
                    <span className="font-semibold text-emerald-600">${item.amount}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Users;
